import {
	AdStatus, CampaignStatus, CreateAdInput, CreateCampaignInput,
	CreateGroupInput, GroupStatus,
} from 'wb-spotify/graphql/generated';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import {
	getSpotifyAdVariablesFromSupergridMutation,
	getSpotifyCampaignVariablesFromSupergridMutation,
	getSpotifyGroupVariablesFromSupergridMutation,
} from 'wb-spotify/app/routes/superGrid/mutator/hooks/utils';
import { GridApi, IRowNode } from 'ag-grid-community';

export const SPOTIFY_LEVEL_TO_ACTIVE_STATUS_MAP = {
	[SpotifyLevel.CAMPAIGN]: CampaignStatus.Active,
	[SpotifyLevel.GROUP]:    GroupStatus.Active,
	[SpotifyLevel.AD]:       AdStatus.Active,
};

const SPOTIFY_SUPERGRID_LEVEL_ORDER = [SpotifyLevel.CAMPAIGN, SpotifyLevel.GROUP, SpotifyLevel.AD];

export const SPOTIFY_DRAFT_STATUSES = [CampaignStatus.Draft, GroupStatus.Draft, AdStatus.Draft];

function getChildrenInDraftStatus(data: any) {
	return data?.filter(child => SPOTIFY_DRAFT_STATUSES.includes(child?.status));
}

function getVariablesDataForMutation(data: any, level: SpotifyLevel) {
	switch (level) {
		case SpotifyLevel.CAMPAIGN:
			return {
				...getSpotifyCampaignVariablesFromSupergridMutation(data, CampaignStatus.Active),
				campaignId: data.id,
			};
		case SpotifyLevel.GROUP:
			return {
				...getSpotifyGroupVariablesFromSupergridMutation(data, GroupStatus.Active),
				groupId: data.id,
			};
		case SpotifyLevel.AD:
			return {
				...getSpotifyAdVariablesFromSupergridMutation(data, AdStatus.Active),
				adId:    data.id,
				groupId: data.parent?.id,
			};
		default:
			return undefined;
	}
}

export function getVariablesForActivateDraftEntities(data: any): {
	campaigns: CreateCampaignInput[];
	groups: CreateGroupInput[];
	ads: CreateAdInput[];
} {
	const currentLevelIndex = SPOTIFY_SUPERGRID_LEVEL_ORDER.indexOf(data?.level);

	const campaigns: CreateCampaignInput[] = [];
	const groups: CreateGroupInput[] = [];
	const ads: CreateAdInput[] = [];

	// Process the top level entity if it's in draft status
	if (SPOTIFY_DRAFT_STATUSES.includes(data?.status)) {
		const topLevelVariables = getVariablesDataForMutation(data, data?.level);

		if (topLevelVariables) {
			switch (data?.level) {
				case SpotifyLevel.CAMPAIGN:
					campaigns.push(topLevelVariables as CreateCampaignInput);

					break;
				case SpotifyLevel.GROUP:
					groups.push(topLevelVariables as CreateGroupInput);

					break;
				case SpotifyLevel.AD:
					ads.push(topLevelVariables as CreateAdInput);

					break;
			}
		}
	}

	// Process children level by level
	let currentDraftChildren = getChildrenInDraftStatus(data?.children ?? []);

	for (let i = currentLevelIndex + 1; i < SPOTIFY_SUPERGRID_LEVEL_ORDER.length; i++) {
		if (currentDraftChildren?.length) {
			const level = SPOTIFY_SUPERGRID_LEVEL_ORDER[i];
			const levelVariables = currentDraftChildren
				.map(child => getVariablesDataForMutation(child, level))
				.filter((vars): vars is NonNullable<typeof vars> => vars !== undefined);

			switch (level) {
				case SpotifyLevel.CAMPAIGN:
					campaigns.push(...(levelVariables as CreateCampaignInput[]));

					break;
				case SpotifyLevel.GROUP:
					groups.push(...(levelVariables as CreateGroupInput[]));

					break;
				case SpotifyLevel.AD:
					ads.push(...(levelVariables as CreateAdInput[]));

					break;
			}
		}

		const nextLevelChildren = currentDraftChildren?.flatMap(item => item?.children ?? []) ?? [];
		currentDraftChildren = getChildrenInDraftStatus(nextLevelChildren);
	}

	return { campaigns, groups, ads };
}

export function isEntitySyncPending(data: any) {
	return data?.externalId === null;
}

/**
 * Recursively collects all children that are in draft status
 */
export const collectChildrenInDraftStatus = (children: any[] | null | undefined): any[] => {
	if (!children) {
		return [];
	}

	const draftChildren: any[] = [];

	children.forEach(child => {
		if (SPOTIFY_DRAFT_STATUSES.includes(child?.status)) {
			draftChildren.push(child);
		}

		// Recursively collect children of children
		if (child?.children) {
			draftChildren.push(...collectChildrenInDraftStatus(child.children));
		}
	});

	return draftChildren;
};

/**
 * Recursively updates children statuses in the grid starting from a parent node
 */
export const updateChildrenStatusesInGrid = (
	children: any[],
	api: GridApi,
	parentNode: IRowNode,
) => {
	// Create a set of child IDs for quick lookup
	const childrenIds = new Set<string>();
	children.forEach(child => {
		if (child?.id) {
			childrenIds.add(child.id);
		}
	});

	// Track which nodes we've already updated to avoid duplicates
	const updatedNodeIds = new Set<string>();

	// Function to update a node if it matches
	const updateNodeIfMatch = (rowNode: IRowNode) => {
		if (!rowNode.data || !rowNode.data.id) {
			return;
		}

		// Skip if already updated
		if (updatedNodeIds.has(rowNode.data.id)) {
			return;
		}

		// Check if this node matches any of our children by ID
		if (childrenIds.has(rowNode.data.id)) {
			const childRoute = rowNode.getRoute()?.slice(0, -1);
			const childSupergridLevel = rowNode.data.supergridLevel || rowNode.data.level;

			api.applyServerSideTransaction({
				route:  childRoute,
				update: [{
					...rowNode.data,
					status: SPOTIFY_LEVEL_TO_ACTIVE_STATUS_MAP[childSupergridLevel],
				}],
			});

			updatedNodeIds.add(rowNode.data.id);
		}
	};

	// Recursively traverse child nodes from the parent
	const traverseAndUpdate = (rowNode: IRowNode) => {
		updateNodeIfMatch(rowNode);

		// Recursively process children
		rowNode.childrenAfterFilter?.forEach(childNode => {
			traverseAndUpdate(childNode);
		});
	};

	// Start traversal from parent's children
	parentNode.childrenAfterFilter?.forEach(childNode => {
		traverseAndUpdate(childNode);
	});

	// Also search the entire grid as fallback for nodes that might not be direct children
	api.forEachNode((rowNode) => {
		updateNodeIfMatch(rowNode);
	});
};
